import { FormEvent, useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { mascaraMoedaBR, moedaParaNumero } from '../lib/moeda';

// Tela "Serviços" (menu lateral). Só existe para a empresa com a permissão
// SERVIÇOS — o gate mora em auth.tsx (isServicosDisponivel) e vale para o menu
// (Layout) e para a rota (App). É a TABELA DE PREÇOS DE SERVIÇO da empresa:
// CRUD de preço, sem tocar em OS, estoque ou faturamento.
//
// Regras do cliente:
//   - UMA LINHA = UM SERVIÇO, com o tipo (preventiva/corretiva) explícito na
//     linha. Antes o resumo vinha da planilha como matriz (preventiva e corretiva
//     na mesma linha), e não dava para saber o que era cada preço.
//   - CRUD completo: criar, editar todos os campos e ativar/desativar.
//   - Serviço NUNCA é excluído, só DESATIVADO (o DELETE do endpoint desativa).
//
// A tela lista TODOS os grupos (resumo_prev_corr, manutencao_btu,
// outros_servicos) que o endpoint devolve. Servico criado por aqui sempre
// entra no grupo `resumo_prev_corr` (tem tipo preventiva/corretiva); os outros
// dois grupos so existem via importacao da planilha.

const GRUPO = 'resumo_prev_corr';
const ITENS_POR_PAGINA = 10;

interface Servico {
  id: number;
  grupo: string;
  tipo_manutencao: string | null;
  bloco: string;
  equipamento: string | null;
  descricao: string | null;
  faixa_contratual: string | null;
  unidade: string | null;
  valor_unitario: string | number | null;
  observacao: string | null;
  ordem: number;
  origem: string | null;
  status: string;
}

interface Retorno {
  servicos: Servico[];
  importacao: {
    origem: string | null;
    planilha: string | null;
    total_planilha: number;
    total_importado: number;
  };
  referencia: {
    titulo: string | null;
    subtitulo: string | null;
    nota_valores: string | null;
    notas: string[];
    orientacoes: { tema: string; detalhe: string }[];
  };
}
interface AtivoOpcao {
  id: number;
  nome: string;
  patrimonio?: string | null;
  status?: string | null;
}

interface Form {
  id?: number;
  tipo_manutencao: string;
  bloco: string;
  equipamento: string;
  descricao: string;
  valor_unitario: string;
  observacao: string;
}

const FORM_VAZIO: Form = {
  tipo_manutencao: 'preventiva',
  bloco: '',
  equipamento: '',
  descricao: '',
  valor_unitario: '',
  observacao: '',
};

function erro(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

// O Postgres devolve numeric como string ("350.00"); null aqui é ausência de
// preço contratado e NÃO pode virar zero — nem na tela, nem ao salvar.
const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const fmtMoeda = (v: unknown): string => {
  const n = num(v);
  if (n === null) return '—';
  return 'R$ ' + n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
// Valor no padrão brasileiro: "30,40", "1.040,00". Mesmo formato de saída da
// máscara de centavos do projeto, para o campo não "pular" ao abrir a edição.
// (numeroParaMascaraMoeda devolve '' para zero, e aqui zero precisa aparecer.)
const paraInput = (v: unknown): string => {
  const n = num(v);
  return n === null ? '' : n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
// Campo vazio significa "sem preço contratado", que é diferente de zero e precisa
// continuar assim no banco.
const doInput = (v: string): number | null => {
  if (!(v || '').trim()) return null;
  return moedaParaNumero(v);
};

const unicos = (lista: string[]): string[] => Array.from(new Set(lista)).sort((a, b) => a.localeCompare(b, 'pt-BR'));
const ehInativo = (s: Servico): boolean => (s.status || 'ativo') === 'inativo';

const ROTULO_TIPO: Record<string, string> = { preventiva: 'PREVENTIVA', corretiva: 'CORRETIVA' };
const COR_TIPO: Record<string, { background: string; color: string }> = {
  preventiva: { background: '#dcfce7', color: '#15803d' },
  corretiva: { background: '#fef3c7', color: '#b45309' },
};

// Etiqueta do tipo: é o que responde "esse serviço é preventivo ou corretivo?".
function Tipo({ tipo }: { tipo: string | null }) {
  if (!tipo || !ROTULO_TIPO[tipo]) return <span style={{ color: 'var(--texto-suave)' }}>—</span>;
  return <span className="badge" style={COR_TIPO[tipo]}>{ROTULO_TIPO[tipo]}</span>;
}

export default function Servicos() {
  const { user } = useAuth();
  const [dados, setDados] = useState<Retorno | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [importando, setImportando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erroMsg, setErroMsg] = useState('');
  const [msg, setMsg] = useState('');
  const [form, setForm] = useState<Form>(FORM_VAZIO);
  const [ativos, setAtivos] = useState<AtivoOpcao[]>([]);
  const [situacao, setSituacao] = useState('ativo');
  const [tipoFiltro, setTipoFiltro] = useState('');
  const [busca, setBusca] = useState('');
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [verOrientacoes, setVerOrientacoes] = useState(false);

  const podeGerenciar = ['admin_geral', 'admin_empresa', 'supervisor'].includes(user?.perfil || '');

  async function carregar() {
    setErroMsg('');
    setCarregando(true);
    try {
      setDados(await api<Retorno>('/servicos/servicos.php'));
    } catch (e) {
      setErroMsg(erro(e));
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => { carregar(); }, []);
  useEffect(() => {
    api<AtivoOpcao[]>('/ativos/ativos.php')
      .then((lista) => setAtivos(lista.filter((a) => (a.status || 'ativo') === 'ativo')))
      .catch(() => setAtivos([]));
  }, []);
  useEffect(() => {
    setPaginaAtual(1);
  }, [situacao, tipoFiltro, busca]);

  async function executar(fn: () => Promise<void>, sucesso: string) {
    setErroMsg('');
    setMsg('');
    setSalvando(true);
    try {
      await fn();
      setMsg(sucesso);
      await carregar();
    } catch (e) {
      setErroMsg(erro(e));
    } finally {
      setSalvando(false);
    }
  }

  async function importar() {
    const total = dados?.importacao.total_planilha ?? 0;
    const aviso = dados?.importacao.total_importado
      ? 'A tabela já foi importada. Reimportar SOBRESCREVE, com os valores da planilha, os preços editados aqui. Continuar?'
      : `Importar ${total} linhas de serviço e os materiais e peças da planilha do cliente para esta empresa?`;
    if (!window.confirm(aviso)) return;
    setErroMsg('');
    setMsg('');
    setImportando(true);
    try {
      const r = await api<{
        servicos_inseridos: number; servicos_atualizados: number;
        materiais_inseridos: number; materiais_atualizados: number;
      }>('/servicos/servicos_importar.php', { method: 'POST' });
      setMsg(
        `Serviços: ${r.servicos_inseridos} novos, ${r.servicos_atualizados} atualizados. `
        + `Materiais no estoque: ${r.materiais_inseridos} novos, ${r.materiais_atualizados} atualizados.`,
      );
      await carregar();
    } catch (e) {
      setErroMsg(erro(e));
    } finally {
      setImportando(false);
    }
  }

  function editar(s: Servico) {
    setForm({
      id: s.id,
      tipo_manutencao: s.tipo_manutencao || '',
      bloco: s.bloco || '',
      equipamento: s.equipamento || '',
      descricao: s.descricao || '',
      valor_unitario: paraInput(s.valor_unitario),
      observacao: s.observacao || '',
    });
    setErroMsg('');
    setMsg('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function salvar(e: FormEvent) {
    e.preventDefault();
    const bloco = form.bloco.trim();
    if (!bloco) {
      setErroMsg('Informe o tipo de serviço (ex.: CLIMATIZAÇÃO, BEBEDOUROS).');
      return;
    }
    // `faixa_contratual` acompanha a faixa digitada: as duas colunas existem por
    // causa da planilha (uma é o rótulo exibido, a outra a faixa do contrato) e
    // no cadastro manual não faz sentido pedir a mesma coisa duas vezes.
    const body: Record<string, unknown> = {
      tipo_manutencao: form.tipo_manutencao || null,
      bloco,
      equipamento: form.equipamento.trim() || null,
      descricao: form.descricao.trim() || null,
      faixa_contratual: form.descricao.trim() || null,
      valor_unitario: doInput(form.valor_unitario),
      observacao: form.observacao.trim() || null,
    };
    await executar(async () => {
      if (form.id) await api('/servicos/servicos.php', { method: 'PUT', body: { ...body, id: form.id } });
      else await api('/servicos/servicos.php', { method: 'POST', body: { ...body, grupo: GRUPO } });
      setForm(FORM_VAZIO);
    }, form.id ? 'Serviço atualizado.' : 'Serviço criado.');
  }

  // Nunca excluir: só desativar e reativar.
  async function alternarSituacao(s: Servico) {
    await executar(
      () => api('/servicos/servicos.php', {
        method: 'PUT',
        body: { id: s.id, status: ehInativo(s) ? 'ativo' : 'inativo' },
      }),
      ehInativo(s) ? 'Serviço reativado.' : 'Serviço desativado.',
    );
  }

  // Antes so entrava o grupo GRUPO (resumo_prev_corr). Os grupos manutencao_btu
  // e outros_servicos - que nao tem tipo_manutencao (preventiva/corretiva) -
  // ficavam de fora da listagem mesmo ja importados e salvos no banco. Agora
  // a tela mostra os 3 grupos; o Tipo() ja trata tipo_manutencao nulo como "-".
  const todos = useMemo(() => dados?.servicos || [], [dados]);
  const blocos = unicos(todos.map((s) => s.bloco).filter(Boolean));
  const termo = busca.trim().toLowerCase();
  const lista = todos.filter((s) => {
    if (situacao && (s.status || 'ativo') !== situacao) return false;
    if (tipoFiltro && (s.tipo_manutencao || '') !== tipoFiltro) return false;
    if (!termo) return true;
    return [s.bloco, s.equipamento, s.descricao].some((c) => (c || '').toLowerCase().includes(termo));
  });
  const totalPaginas = Math.max(1, Math.ceil(lista.length / ITENS_POR_PAGINA));
  const paginaSegura = Math.min(paginaAtual, totalPaginas);
  const listaPaginada = lista.slice((paginaSegura - 1) * ITENS_POR_PAGINA, paginaSegura * ITENS_POR_PAGINA);

  const ref = dados?.referencia;

  return (
    <div>
      <div className="titulo-pg">
        <h1>Serviços</h1>
        <button className="btn sec sm" onClick={() => carregar()} disabled={carregando}>Atualizar</button>
      </div>
      <p style={{ color: 'var(--texto-suave)', margin: '0 0 12px', fontSize: 14 }}>
        Tabela de preços de serviço desta empresa. Cada linha é <strong>um serviço</strong>, com o tipo
        (preventiva ou corretiva) na própria linha. Serviço não é excluído — só desativado.
        As peças e materiais da mesma planilha ficam no cadastro de <strong>materiais do Estoque</strong>.
      </p>

      {erroMsg && <div className="msg-erro">{erroMsg}</div>}
      {msg && <div className="msg-ok">{msg}</div>}

      {podeGerenciar && (
        <form className="card" onSubmit={salvar}>
          <h3 style={{ marginTop: 0 }}>{form.id ? 'Editar serviço' : 'Novo serviço'}</h3>
          <div className="grid cols2">
            <div>
              <label>Tipo de manutenção</label>
              <select value={form.tipo_manutencao} onChange={(e) => setForm({ ...form, tipo_manutencao: e.target.value })}>
                <option value="preventiva">Preventiva</option>
                <option value="corretiva">Corretiva</option>
                <option value="">Não se aplica</option>
              </select>
            </div>
            <div>
              <label>Tipo de serviço *</label>
              <input className="input" list="lista-blocos-servico" value={form.bloco}
                     onChange={(e) => setForm({ ...form, bloco: e.target.value })}
                     required placeholder="Ex.: CLIMATIZAÇÃO" />
              <datalist id="lista-blocos-servico">
                {blocos.map((b) => <option key={b} value={b} />)}
              </datalist>
            </div>
            <div>
              <label>Equipamento / tipo</label>
              {!!ativos.length && (
                <select
                  value={ativos.some((a) => a.nome === form.equipamento) ? form.equipamento : ''}
                  onChange={(e) => setForm({ ...form, equipamento: e.target.value })}
                  style={{ marginBottom: 6 }}
                >
                  <option value="">Digitar livremente</option>
                  {ativos.map((a) => (
                    <option key={a.id} value={a.nome}>
                      {a.nome}{a.patrimonio ? ' - ' + a.patrimonio : ''}
                    </option>
                  ))}
                </select>
              )}
              <input
                className="input"
                value={form.equipamento}
                onChange={(e) => setForm({ ...form, equipamento: e.target.value })}
                placeholder="Ex.: Split de parede"
              />
            </div>
            <div>
              <label>Faixa de capacidade</label>
              <input className="input" value={form.descricao}
                     onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                     placeholder="Ex.: 22.000 a 36.000 btus" />
            </div>
            <div>
              <label>Valor (R$)</label>
              <input className="input" inputMode="numeric" value={form.valor_unitario}
                     onChange={(e) => setForm({ ...form, valor_unitario: mascaraMoedaBR(e.target.value) })}
                     placeholder="vazio = sem preço contratado" />
            </div>
          </div>
          <label>Observação</label>
          <input className="input" value={form.observacao}
                 onChange={(e) => setForm({ ...form, observacao: e.target.value })}
                 placeholder="Ex.: fonte do preço, divergência de contrato..." />
          <div className="form-actions">
            {form.id && (
              <button type="button" className="btn sec" disabled={salvando} onClick={() => setForm(FORM_VAZIO)}>Cancelar</button>
            )}
            <button className="btn" disabled={salvando}>{form.id ? 'Salvar serviço' : 'Adicionar serviço'}</button>
          </div>
        </form>
      )}

      {carregando && <div className="card" style={{ marginTop: 16 }}>Carregando serviços...</div>}

      {!carregando && (
        <div className="card" style={{ marginTop: 16 }}>
          <h2 style={{ margin: '0 0 4px' }}>Serviços cadastrados</h2>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', margin: '12px 0 8px' }}>
            <input className="input" style={{ flex: '1 1 220px', margin: 0 }} value={busca}
                   onChange={(e) => setBusca(e.target.value)}
                   placeholder="Buscar equipamento, faixa ou tipo de serviço" />
            <select style={{ flex: '0 1 190px', margin: 0 }} value={tipoFiltro} onChange={(e) => setTipoFiltro(e.target.value)}>
              <option value="">Todos os tipos</option>
              <option value="preventiva">Só preventiva</option>
              <option value="corretiva">Só corretiva</option>
            </select>
            <select style={{ flex: '0 1 145px', margin: 0 }} value={situacao} onChange={(e) => setSituacao(e.target.value)}>
              <option value="ativo">Ativos</option>
              <option value="inativo">Desativados</option>
              <option value="">Todos</option>
            </select>
            <small style={{ color: 'var(--texto-suave)' }}>{lista.length} de {todos.length}</small>
          </div>

          <div className="tabela-wrap">
            <table>
              <thead>
                <tr>
                  <th>Tipo</th><th>Tipo de serviço</th><th>Equipamento</th><th>Faixa de capacidade</th>
                  <th>Valor</th><th>Observação</th>
                  {podeGerenciar && <th></th>}
                </tr>
              </thead>
              <tbody>
                {listaPaginada.map((s) => (
                  <tr key={s.id} style={{ opacity: ehInativo(s) ? 0.6 : 1 }}>
                    <td><Tipo tipo={s.tipo_manutencao} /></td>
                    <td>{s.bloco}</td>
                    <td>
                      <strong>{s.equipamento || '—'}</strong>
                      {ehInativo(s) && (
                        <span className="badge" style={{ background: '#fee2e2', color: '#b91c1c', marginLeft: 6 }}>desativado</span>
                      )}
                    </td>
                    <td>{s.descricao || '—'}</td>
                    <td>
                      {num(s.valor_unitario) === null
                        ? <span style={{ color: 'var(--texto-suave)', fontStyle: 'italic' }}>{s.observacao || 'sem preço contratado'}</span>
                        : <strong>{fmtMoeda(s.valor_unitario)}</strong>}
                    </td>
                    <td style={{ maxWidth: 280 }}><small style={{ color: 'var(--texto-suave)' }}>{s.observacao || '—'}</small></td>
                    {podeGerenciar && (
                      <td className="acoes">
                        <button className="btn sec sm" disabled={salvando} onClick={() => editar(s)}>Editar</button>
                        <button
                          className={ehInativo(s) ? 'btn sec sm' : 'btn danger sm'}
                          disabled={salvando}
                          onClick={() => alternarSituacao(s)}
                        >
                          {ehInativo(s) ? 'Reativar' : 'Desativar'}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
                {!lista.length && (
                  <tr>
                    <td colSpan={podeGerenciar ? 7 : 6} className="vazio">
                      {todos.length
                        ? 'Nenhum serviço para este filtro.'
                        : (podeGerenciar
                          ? 'Nenhum serviço cadastrado. Use o formulário acima, ou importe a tabela do cliente.'
                          : 'Nenhum serviço cadastrado. Fale com o gestor da empresa.')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {lista.length > ITENS_POR_PAGINA && (
            <div className="paginacao-lista">
              <span>Página {paginaSegura} de {totalPaginas}</span>
              <div className="paginacao-acoes">
                <button className="btn sec sm" disabled={paginaSegura <= 1} onClick={() => setPaginaAtual(1)}>Primeira</button>
                <button className="btn sec sm" disabled={paginaSegura <= 1} onClick={() => setPaginaAtual((atual) => Math.max(1, atual - 1))}>Anterior</button>
                <button className="btn sec sm" disabled={paginaSegura >= totalPaginas} onClick={() => setPaginaAtual((atual) => Math.min(totalPaginas, atual + 1))}>Próxima</button>
                <button className="btn sec sm" disabled={paginaSegura >= totalPaginas} onClick={() => setPaginaAtual(totalPaginas)}>Última</button>
              </div>
            </div>
          )}
        </div>
      )}

      {podeGerenciar && dados && (
        <div className="card" style={{ marginTop: 16 }}>
          <h3 style={{ margin: '0 0 6px' }}>Importar a tabela do cliente</h3>
          <p style={{ color: 'var(--texto-suave)', margin: '0 0 10px', fontSize: 13 }}>
            Planilha <strong>{dados.importacao.planilha || '—'}</strong>: {dados.importacao.total_planilha} linhas de
            serviço + os materiais e peças. Serviço entra aqui; material entra no Estoque.
            Já importados nesta empresa: <strong>{dados.importacao.total_importado}</strong> serviço(s).
            Não duplica ao reimportar e nunca mexe no saldo de estoque.
            <strong> Atenção:</strong> reimportar sobrescreve, com os valores da planilha, os preços editados aqui.
          </p>
          <div className="form-actions" style={{ marginTop: 0 }}>
            <button className="btn" onClick={importar} disabled={importando || salvando}>
              {importando ? 'Importando...' : (dados.importacao.total_importado ? 'Reimportar tabela' : 'Importar tabela')}
            </button>
          </div>
        </div>
      )}

      {Boolean(ref?.orientacoes?.length) && (
        <div className="card" style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <h2 style={{ margin: 0 }}>Como usar / pontos de atenção</h2>
            <button className="btn sec sm" onClick={() => setVerOrientacoes((v) => !v)}>
              {verOrientacoes ? 'Ocultar' : 'Mostrar'}
            </button>
          </div>
          {verOrientacoes && (
            <div className="tabela-wrap" style={{ marginTop: 10 }}>
              <table>
                <thead><tr><th style={{ width: 220 }}>Tema</th><th>Detalhe</th></tr></thead>
                <tbody>
                  {ref!.orientacoes.map((o, i) => (
                    <tr key={i}><td><strong>{o.tema}</strong></td><td>{o.detalhe}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
